import { type InputNumberProps } from 'antd';
import { useField } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import useEnergyUnitOptions from '../../utilities/useEnergyUnitOptions';
import FormItem, { type FormItemProps } from './FormItem';
import { InputNumber } from './InputNumberField';
import { Select } from './SelectField';

export interface InputFieldProps extends Omit<InputNumberProps, 'value' | 'onChange' | 'autoComplete' | 'addonAfter'> {
    name: string;
    label?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    tooltip?: FormItemProps['tooltip'];
}

const EnergyConsumptionField = ({ name, required, label, itemProps, tooltip, ...props }: InputFieldProps) => {
    const [valueField, valueMeta, valueHelper] = useField({ name: `${name}.value` });
    const [unitField, unitMeta, unitHelper] = useField({ name: `${name}.unit` });

    const meta = useMemo(
        () => ({
            error: unitMeta.error || valueMeta.error,
            touched: (unitMeta.touched && valueMeta.touched) || (unitField.value && valueMeta.touched),
            initialValue: valueMeta.initialValue,
            value: valueMeta.value,
        }),
        [
            unitMeta.error,
            unitMeta.touched,
            valueMeta.error,
            valueMeta.touched,
            valueMeta.initialValue,
            valueMeta.value,
            unitField.value,
        ]
    );

    const enhancedOnChange = useCallback(
        value => {
            unitHelper.setValue(value);
        },
        [unitHelper]
    );

    const energyUnitOptions = useEnergyUnitOptions();

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required} tooltip={tooltip}>
            <InputNumber
                autoComplete="off"
                // then spread the field properties itself
                {...valueField}
                // manually attach on change
                addonAfter={
                    <Select
                        disabled={props.disabled}
                        style={{ width: '140px' }}
                        {...unitField}
                        onChange={enhancedOnChange}
                        options={energyUnitOptions}
                        showSearch
                    />
                }
                controls={false}
                disabled={props.disabled}
                onChange={value => valueHelper.setValue(value)}
            />
        </FormItem>
    );
};

export default memo(EnergyConsumptionField);
